// Port of SuiteModel/MapProject.swift's `MapCast`: Link Notes to Cast… — notes whose words are a character's, place's,
// object's or motif's name or alias.
import type { StoryEntity } from '../model/documents.ts';
import { charIsWhitespace } from '../unicode.ts';
import type { MapContent } from './map.ts';

/** What `drop { ".,;:!?".contains($0) }` takes off the end. */
const TRAILING = new Set(['.', ',', ';', ':', '!', '?']);

export const MapCast = {
  /** A name as it's compared: accents and case aside, spaces collapsed, a trailing full stop or comma dropped. */
  key(text: string): string {
    const bare = [...text.normalize('NFD')].filter((c) => { const cp = c.codePointAt(0)!; return cp < 0x300 || cp > 0x36f; }).join('');
    const words: string[] = [];
    let word = '';
    for (const c of bare.toLowerCase()) {
      if (charIsWhitespace(c)) { if (word) words.push(word); word = ''; } else word += c;
    }
    if (word) words.push(word);
    const chars = [...words.join(' ')];
    let end = chars.length;
    while (end > 0 && TRAILING.has(chars[end - 1])) end--;
    return chars.slice(0, end).join('');
  },

  /** Each unlinked note whose words name exactly one entity, with that entity, in the notes' order. */
  matches(content: MapContent, entities: readonly Pick<StoryEntity, 'id' | 'name' | 'aliases'>[]): { note: string; entity: string }[] {
    const named = new Map<string, Set<string>>();
    for (const entity of entities) {
      for (const name of [entity.name, ...entity.aliases]) {
        const k = MapCast.key(name);
        if (k !== '') named.set(k, (named.get(k) ?? new Set()).add(entity.id));
      }
    }
    return content.notes.flatMap((note) => {
      if (note.link !== null) return [];
      const ids = named.get(MapCast.key(note.text));
      return ids && ids.size === 1 ? [{ note: note.id, entity: [...ids][0] }] : [];
    });
  },
};
